import { type TPath } from './path'

const getCompressedSamples = (peaks: number[], samplesLength: number) => {
    const numberOfSamplesInGroup = Math.floor(peaks.length / samplesLength)

    const compressedSamples = []
    for (let sampleIndex = 0; sampleIndex < samplesLength; sampleIndex++) {
        const firstSampleInGroupIndex = numberOfSamplesInGroup * sampleIndex

        let groupSamplesSum = 0
        for (
            let sampleInGroupCounter = 0;
            sampleInGroupCounter < numberOfSamplesInGroup;
            sampleInGroupCounter++
        ) {
            groupSamplesSum += Math.abs(peaks[firstSampleInGroupIndex + sampleInGroupCounter])
        }

        compressedSamples.push(groupSamplesSum / numberOfSamplesInGroup)
    }

    return [compressedSamples]
}

const getNormalizeData = (filteredData: number[][]) => {
    const multipliers = []
    const filteredDataLength = filteredData.length
    for (let i = 0; i < filteredDataLength; i++) {
        const multiplier = Math.max(...filteredData[i])
        multipliers.push(multiplier)
    }
    const maxMultiplier = Math.max(...multipliers) ** -1

    const normalizeData = []
    for (let i = 0; i < filteredDataLength; i++) {
        const normalizeDataBlock = filteredData[i].map(n => n * maxMultiplier)
        normalizeData.push(normalizeDataBlock)
    }
    return normalizeData
}

type TWaveformOptions = {
    samplesCount: number
    height: number
    width: number
    type?: 'mirror'
    paths: TPath[]
    normalize?: boolean
}

export const generateWaveformPath = (peaks: number[], options: TWaveformOptions) => {
    const {
        samplesCount,
        height: waveformHeight,
        width: waveformWidth,
        paths,
        normalize = true,
    } = options

    const filteredData = getCompressedSamples(peaks, samplesCount)
    const normalizeData = normalize ? getNormalizeData(filteredData) : filteredData

    let path = ``

    const fixHeight = waveformHeight / 2
    const fixWidth = waveformWidth / samplesCount
    const pathsLength = paths.length
    const fixPathsLength = pathsLength * 2

    const normalizeDataLength = normalizeData.length

    for (let frame = 0; frame < normalizeDataLength; frame++) {
        if (frame > 0) {
            const pathLength = path.length
            const lastValue = path.charAt(pathLength - 1)
            if (lastValue === ';' || pathLength === 0) {
                path += ' M 0 0 ;'
            } else {
                path += ';'
            }
        }

        let lastPosX = -9999
        let lastPosY = -9999

        for (let i = 0; i < samplesCount; i++) {
            const even = i % 2 ? 1 : -1
            let mirror = 1
            for (let j = 0; j < fixPathsLength; j++) {
                let k = j
                if (j >= pathsLength) {
                    k = j - pathsLength
                    mirror = -1
                }
                const minshow = paths[k].minshow ?? 0
                const maxshow = paths[k].maxshow ?? 1
                const normalizePath = paths[k].normalize ?? false
                const normalizeDataValue = normalizePath ? 1 : normalizeData[frame][i]
                if (minshow <= normalizeData[frame][i] && maxshow >= normalizeData[frame][i]) {
                    const posX = i * fixWidth + (fixWidth * paths[k].x) / 100
                    const posY =
                        fixHeight +
                        ((normalizeDataValue * paths[k].sy) / 100) *
                            (waveformHeight / 2) *
                            -even *
                            mirror

                    const endPosX = posX
                    const endPosY =
                        fixHeight +
                        ((normalizeDataValue * paths[k].ey) / 100) *
                            (waveformHeight / 2) *
                            -even *
                            mirror

                    if (posX !== lastPosX || posY !== lastPosY) {
                        path += `M ${posX} ${posY} `
                    }

                    path += `V ${endPosY} `

                    lastPosX = endPosX
                    lastPosY = endPosY
                }
            }
        }
    }
    return path
}
